import { useEffect, useState } from "react";
import { useOnboarding } from "@/onboarding/context";
import { GuidanceSwitch } from "@/onboarding/GuidanceSwitch";
import { motion } from "framer-motion";
import { Compass as CompassIcon, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { FULL_JOURNEY } from "@/lib/journey";
import WelcomeIntro from "@/components/feedback/WelcomeIntro";

// Genuine STUDENT entry — the learner has been given an assignment and is
// completing it. No teacher-simulation framing ("Experience The Writing Studio",
// "experience as one of your students", "create an assignment", "Help me
// create one"). For OT mode, a brief student-facing OT introduction precedes
// the assignment-entry screen.
export default function StudentEntry({ mode, initialAssignment = "", onSubmit, submitting }) {
  const isOT = mode === "ot";
  const [step, setStep] = useState(isOT ? "intro" : "assignment");
  const [assignment, setAssignment] = useState(initialAssignment);
  const canContinue = assignment.trim().length > 0;
  const onboarding = useOnboarding();
  useEffect(() => { if (isOT && step === "assignment" && onboarding) onboarding.offerInitialTour(); }, [isOT, step, onboarding]);   // first use: the guided walkthrough offers itself once

  if (step === "intro") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="flex-1 flex flex-col justify-center py-10 max-w-xl"
        data-testid="student-ot-intro"
      >
        <p className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-3">
          The Writing Studio
        </p>
        <h1 className="font-serif-display text-4xl sm:text-5xl leading-tight text-stone-900">
          Here&rsquo;s where we&rsquo;re going
        </h1>
        {/* HOW TO BEGIN — thinking / exploring vs writing the essay. Start wherever you are; three spaces: Coach · Scratch Pad · Essay. */}
        <div data-testid="student-ot-how-to-begin" className="mt-5 space-y-3 text-[15px] leading-relaxed text-stone-700">
          <p><span className="font-semibold text-stone-900">Start wherever you are.</span> You don&rsquo;t need to have your answer worked out before you
            begin. Write an idea, a question, a sentence, a few thoughts &mdash; whatever helps you get started.</p>
          <p className="text-stone-600">As you work, you can use three spaces:</p>
          <dl data-testid="student-ot-three-spaces" className="space-y-2 border-l-2 border-[#8C3A2A]/30 pl-4">
            <div><dt className="inline font-semibold text-stone-900">Coach</dt><dd className="inline"> &mdash; Talk with TWS about your ideas and your writing.</dd></div>
            <div><dt className="inline font-semibold text-stone-900">Scratch Pad</dt><dd className="inline"> &mdash; Play with ideas, make lists, try out wording, or jot down thoughts you aren&rsquo;t
              ready to put in your essay. Nothing here becomes part of your essay unless you choose to move it there.</dd></div>
            <div><dt className="inline font-semibold text-stone-900">Essay</dt><dd className="inline"> &mdash; Write and organize the work you want to become part of your essay.</dd></div>
          </dl>
          <p>TWS will help you develop your ideas, see relationships among them, and build your essay &mdash; through {FULL_JOURNEY.length} stages, from
            working out your thinking to a finished piece. The thinking and writing remain yours.</p>
        </div>
        <ol data-testid="journey-overview" className="mt-6 space-y-2.5">
          {FULL_JOURNEY.map((s, i) => (
            <li key={s.key} data-testid={`journey-overview-${s.key}`} className="flex items-start gap-3">
              <span className="mt-0.5 shrink-0 grid place-items-center h-6 w-6 rounded-full border border-[#8C3A2A]/40 bg-[#8C3A2A]/[0.06] text-[#8C3A2A] text-[11px] font-semibold">{i + 1}</span>
              <div className="min-w-0">
                <p className="text-[14.5px] font-semibold text-stone-900 leading-tight">{s.title}</p>
                <p className="text-[12.5px] text-stone-500 leading-snug">{s.blurb}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-8">
          <WelcomeIntro />
        </div>
        <button
          onClick={() => setStep("assignment")}
          data-testid="student-ot-intro-begin"
          className="group mt-2 self-start inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-7 py-3.5 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] hover:-translate-y-px transition-[background-color,transform]"
        >
          Begin
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="flex-1 flex flex-col py-6 max-w-xl"
      data-testid="student-entry"
    >
      {isOT && (
        <button
          onClick={() => setStep("intro")}
          data-testid="student-entry-back"
          className="self-start inline-flex items-center gap-1.5 text-[12px] font-mono-panel uppercase tracking-[0.14em] text-stone-400 hover:text-stone-700 transition-colors mb-6"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
      )}
      <h1 className="font-serif-display text-4xl sm:text-5xl leading-tight text-stone-900">
        Your Assignment
      </h1>
      <p className="text-stone-700 mt-5 text-[16px] leading-relaxed">
        Paste or type the assignment you are working on.
      </p>
      <p className="text-stone-500 mt-1.5 text-[14px] leading-relaxed">
        Include the complete instructions your teacher gave you.
      </p>

      <textarea
        data-testid="student-assignment-input" data-tour="assignment"
        value={assignment}
        onChange={(e) => setAssignment(e.target.value)}
        rows={7}
        placeholder="Paste or type your assignment here…"
        className="mt-5 w-full bg-white border border-stone-300 rounded-sm p-5 text-[16px] leading-8 text-stone-900 placeholder:text-stone-400 outline-none focus:ring-1 focus:ring-stone-900 focus:border-stone-900 transition-colors resize-y min-h-[180px] font-serif-display"
      />

      <button
        onClick={() => canContinue && onSubmit(assignment.trim())}
        disabled={!canContinue || submitting}
        data-testid="student-assignment-continue"
        className="group mt-6 self-start inline-flex items-center gap-2 bg-[#8C3A2A] text-white px-7 py-3.5 rounded-sm font-medium tracking-wide hover:bg-[#6B2C20] enabled:hover:-translate-y-px transition-[background-color,transform] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {isOT ? "Continue" : "Continue to Writing"}
        <ArrowRight className="h-4 w-4 transition-transform group-enabled:group-hover:translate-x-1" />
      </button>
      {isOT && onboarding ? (
        <button type="button" data-testid="student-entry-replay-tour" onClick={() => onboarding.replay()}
          className="mt-3 self-start text-[12px] text-stone-500 underline decoration-stone-300 underline-offset-4 hover:text-[#8C3A2A]">How TWS works — a 2-minute walkthrough</button>
      ) : null}
      {isOT && onboarding ? <GuidanceSwitch /> : null}

      <div
        data-testid="scope-statement"
        className="mt-8 rounded-sm border border-stone-200 bg-stone-50 px-5 py-4 text-[13.5px] leading-relaxed text-stone-600"
      >
        The Writing Development Studio is designed primarily for <strong className="text-stone-700">expository,
        explanatory, and argumentative</strong> writing. Narrative examples and personal experiences may be
        used as evidence, but some style suggestions reflect expository-writing conventions and should be
        treated as <strong className="text-stone-700">coaching judgments rather than mandatory corrections</strong>.
      </div>

      <div className="mt-10 flex items-center gap-2 text-stone-400 font-mono-panel text-[10px] uppercase tracking-[0.16em]">
        <CompassIcon className="h-3.5 w-3.5" />
        The Writing Studio
      </div>
    </motion.div>
  );
}
